// Canonical date formatters for the whole app. Locale pinned to en-GB so output
// is deterministic regardless of the user's browser locale:
//   formatDate     → "05 Apr 2026"
//   formatDateTime → "05 Apr 2026, 2:30 PM"
//   formatTime     → "2:30 PM"
// Returns an em-dash for null/undefined/empty/invalid input.
const EMPTY = '—';
const LOCALE = 'en-GB';
const DATE_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})(?:$|T)/;
const dateFmt = new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
});
const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});
const timeFmt = new Intl.DateTimeFormat(LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});
function toDate(input) {
    if (!input)
        return null;
    const d = input instanceof Date ? input : new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
}
function parseDateInput(input) {
    const match = DATE_INPUT_RE.exec(input);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day, 12));
    if (date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day) {
        return null;
    }
    return { year, month, day };
}
function dateInputToDisplayDate(input) {
    const parsed = parseDateInput(input);
    return parsed ? new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day, 12)) : null;
}
function localDateInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
// en-GB formats 12h as "2:30 pm" — uppercase the meridiem for consistency.
function uppercaseMeridiem(s) {
    return s.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}
/** "05 Apr 2026" — use for dates without a time component. */
export function formatDate(input) {
    if (typeof input === 'string') {
        const dateOnly = isoToDateInput(input);
        const displayDate = dateOnly ? dateInputToDisplayDate(dateOnly) : null;
        if (displayDate)
            return dateFmt.format(displayDate);
    }
    const d = toDate(input);
    return d ? dateFmt.format(d) : EMPTY;
}
/** "05 Apr 2026, 2:30 PM" — use for timestamps (comments, notes, audit log). */
export function formatDateTime(input) {
    const d = toDate(input);
    return d ? uppercaseMeridiem(dateTimeFmt.format(d)) : EMPTY;
}
/** "2:30 PM" — use for time-only displays. */
export function formatTime(input) {
    const d = toDate(input);
    return d ? uppercaseMeridiem(timeFmt.format(d)) : EMPTY;
}
export function dateToIso(dateStr) {
    const trimmed = dateStr.trim();
    if (!parseDateInput(trimmed))
        return null;
    return `${trimmed}T12:00:00.000Z`;
}
export function isoToDateInput(dateStr) {
    if (!dateStr)
        return '';
    const trimmed = dateStr.trim();
    const literalDate = ISO_DATE_PREFIX_RE.exec(trimmed)?.[1];
    if (literalDate && parseDateInput(literalDate)) {
        return literalDate;
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? '' : localDateInputValue(date);
}
//# sourceMappingURL=date-display.js.map